import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import {
  selectCollectionDetails,
  selectRegenerateCollectionPending,
  selectRegenerateImageCollectionPending,
  selectSetCollectionPublicCoverPending,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";
import {
  ModalContainer,
  CloseButton,
  ArrowButtonWrapper,
  ArrowButton,
  MediaArea,
  ImageElement,
  VideoElement,
  Footer,
  InfoBar,
  ModelName,
  SeedPill,
  Toolbar,
  ToolbarButton,
  ActionRow,
  ButtonGroup,
  Divider,
} from "./CollectionModal.styles";
import ExplosionButton from "../ExplosionButton/ExplosionButton";
import { GenerateDrop } from "../CreateInputBar/CreateInputBar.styles";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/settings.svg";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as FolderIcon } from "../../assets/icons/folder.svg";
import { ReactComponent as LeafIcon } from "../../assets/icons/leaf.svg";
import { ReactComponent as ThumbUpIcon } from "../../assets/icons/thumbs-up.svg";
import { ReactComponent as ThumbUpSelectedIcon } from "../../assets/icons/thumbs-up-selected.svg";
import { ReactComponent as ThumbDownIcon } from "../../assets/icons/thumbs-down.svg";
import { ReactComponent as ThumbDownSelectedIcon } from "../../assets/icons/thumbs-down-selected.svg";

import { formatModelName, isImageOutput } from "../../utils/utils";
import {
  MEDIA_TYPE,
  NUDIFY_PRICE,
  REGENERATE_DISCOUNT_PERCENTAGE,
  VIDEO_DURATION_PRICE,
  VIDEO_FPS_PRICE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import {
  fetchCollection,
  regenerateCollection,
  regenerateImageCollection,
  setCollectionPublicCover,
} from "../../store/slices/model/asyncThunks";
import { toast } from "sonner";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";

import deleteAnimation from "../../assets/animations/trash.json";
import downloadAnimation from "../../assets/animations/download.json";
import LottieIcon from "../LottieIcon/LottieIcon";
import GenerationToast from "../utils/GenerationToast";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import ButtonAnimation from "../utils/ButtonAnimation";

const CollectionModal = ({
  isOpen,
  onClose,
  video,
  onDeleteClick,
  onRateClick,
  onFolderClick,
  onSettingsClick,
  setSettingsPrompt,
  setShowExtendVideoModal,
  rating,
  onNavigate,
  hasPrev,
  hasNext,
  navLoading,
}) => {
  useLockBodyScroll(isOpen);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const { loading } = useSelector(selectCollectionDetails);

  const regenerateLoading = useSelector(selectRegenerateCollectionPending);
  const regenerateLoading2 = useSelector(
    selectRegenerateImageCollectionPending,
  );

  const [aspectRatio, setAspectRatio] = useState("");
  const [aspectNum, setAspectNum] = useState(1);
  const [isVerticalImage, setIsVerticalImage] = useState(true);
  const [inViewKey, setInViewKey] = useState(0);
  const [showSetCoverConfirm, setShowSetCoverConfirm] = useState(false);
  const selectCoverPending = useSelector(selectSetCollectionPublicCoverPending);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { ref: videoContainerRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: "100px",
    triggerOnce: true,
  });

  useEffect(() => {
    setVideoLoaded(false);
    setInViewKey((prev) => prev + 1);
  }, [video?._id]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" && hasPrev && !navLoading) {
        onNavigate(-1);
      } else if (e.key === "ArrowRight" && hasNext && !navLoading) {
        onNavigate(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, hasPrev, hasNext, navLoading, onNavigate]);

  useEffect(() => {
    const applyAspect = (w, h) => {
      if (!w || !h) return;
      setAspectRatio(`${w} / ${h}`);
      setAspectNum(w / h);
      setIsVerticalImage(w <= h);
    };

    let width =
      video?.input?.image?.resizedImage?.width || video?.input?.image?.width;
    let height =
      video?.input?.image?.resizedImage?.height || video?.input?.image?.height;
    applyAspect(width, height);

    if (video?.output?.type === MEDIA_TYPE.IMAGE && video?.output?.image?.url) {
      const img = new Image();
      img.onload = () => applyAspect(img.naturalWidth, img.naturalHeight);
      img.src = video.output.image.url;
    } else if (
      video?.output?.type === MEDIA_TYPE.VIDEO &&
      video?.output?.video?.url
    ) {
      const videoEl = document.createElement("video");
      videoEl.onloadedmetadata = () =>
        applyAspect(videoEl.videoWidth, videoEl.videoHeight);
      videoEl.src = video.output.video.url;
    }
  }, [video]);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleDeleteClick = () => {
    if (onDeleteClick) {
      onDeleteClick();
    }
  };

  const handleRateClick = (r) => {
    if (onRateClick) {
      onRateClick(r);
    }
  };

  const handleFolderClick = () => {
    if (onFolderClick) {
      onFolderClick();
    }
  };

  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
      setSettingsPrompt(video?.customPrompt);
    }
  };

  const handleDownload = async () => {
    let mediaUrl = null;
    if (video.output.type === MEDIA_TYPE.IMAGE) {
      mediaUrl = video.output.image.url;
    } else {
      mediaUrl = video.output.video.url;
    }

    if (!mediaUrl) return;
    window.location.href = mediaUrl;
  };

  const regenerateVideo = () => {
    if (isImageOutput(video.modelType)) {
      dispatch(
        regenerateImageCollection({
          collectionId: video._id,
          seed: Math.floor(Math.random() * 10000000) + 1,
        }),
      )
        .unwrap()
        .then(() => {
          <GenerationToast
            successMessage={"Regeneration started.."}
            label="View"
            onClick={() => {
              navigate("/mine");
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          />;
          onClose();
        })
        .catch((err) => {
          toast.error(err);
        });
    } else {
      dispatch(
        regenerateCollection({
          collectionId: video._id,
          seed: Math.floor(Math.random() * 10000000) + 1,
        }),
      )
        .unwrap()
        .then(() => {
          <GenerationToast
            successMessage={"Regeneration started.."}
            label="View"
            onClick={() => {
              navigate("/mine");
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          />;
          onClose();
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  };

  const handleSetCoverConfirm = () => {
    dispatch(setCollectionPublicCover({ collectionId: video?._id }))
      .unwrap()
      .then(() => {
        toast.success("Cover image updated.");
        setShowSetCoverConfirm(false);
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to set cover image.");
      });
  };

  const copyToClipBoard = async () => {
    if (video.seed) {
      try {
        await navigator.clipboard.writeText(video.seed);
        toast.success("Copied to clipboard");
      } catch (err) {
        console.error("Failed to copy link:", err);
      }
    }
  };

  if (!video) return null;

  const isAnyLoading = regenerateLoading || regenerateLoading2 || loading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3000 }}
      collectionModal={isMobile}
    >
      <ModalContainer
        ref={videoContainerRef}
        key={inViewKey}
        $aspectRatio={aspectRatio}
        $aspectNum={aspectNum}
        $minHeight={!!video?.customPrompt ? "415px" : "371px"}
      >
        {/* ── Navigation arrows (external to modal) ── */}
        <ArrowButtonWrapper
          onClick={() => hasPrev && !navLoading && onNavigate(-1)}
          style={{ opacity: hasPrev ? 1 : 0.3 }}
        >
          <ArrowButton>
            {navLoading ? (
              <l-bouncy size="20" stroke="2" speed="1" color="#fff" />
            ) : (
              <ChevronIcon />
            )}
          </ArrowButton>
        </ArrowButtonWrapper>

        <ArrowButtonWrapper
          $right
          onClick={() => hasNext && !navLoading && onNavigate(1)}
          style={{ opacity: hasNext ? 1 : 0.3 }}
        >
          <ArrowButton>
            {navLoading ? (
              <l-bouncy size="20" stroke="2" speed="1" color="#fff" />
            ) : (
              <ChevronIcon style={{ rotate: "180deg" }} />
            )}
          </ArrowButton>
        </ArrowButtonWrapper>

        {/* Close button — anchored to ModalContainer so it sits at the
            top-right of the modal on both layouts (over the media on
            mobile, top-right of the right panel on desktop). */}
        <CloseButton onClick={onClose}>
          <CloseSvg />
        </CloseButton>

        {/* ── LEFT: Media ── */}
        <MediaArea>
          {!!video.seed && (
            <SeedPill onClick={copyToClipBoard}>
              <LeafIcon />
              {video.seed}
            </SeedPill>
          )}
          {video?.output?.type === MEDIA_TYPE.IMAGE ? (
            <ImageElement
              src={video.output.image.url}
              $isVerticalImage={isVerticalImage}
            />
          ) : (
            <>
              {inView && (
                <VideoElement
                  ref={videoRef}
                  controls={false}
                  onLoadedData={handleVideoLoad}
                  muted
                  loop
                  playsInline
                  poster={
                    video?.input?.image?.resizedImage?.url ||
                    video?.input?.image?.url
                  }
                  autoPlay
                  preload="auto"
                  $loaded={videoLoaded}
                  $isVerticalImage={isVerticalImage}
                >
                  <source
                    src={
                      video?.output?.video?.compressedUrl ||
                      video?.output?.video?.url
                    }
                    type="video/mp4"
                  />
                </VideoElement>
              )}
            </>
          )}
        </MediaArea>

        {/* ── RIGHT: Actions panel (overlays the video on mobile) ── */}
        <Footer>
          {/* Header row — title + credit/seed pill (has its own margin-bottom) */}
          <InfoBar>
            <ModelName>{formatModelName(video?.name)}</ModelName>
          </InfoBar>

          {/* Grouped actions — each group controls its own bottom spacing */}
          <Toolbar>
            {/* Feedback group — thumbs up / down  (mb 12) */}
            <ButtonGroup $mb={12}>
              <ToolbarButton $ratingUp={rating === 1} onClick={() => {}}>
                <ButtonAnimation
                  variant="thumb"
                  color="40, 217, 96"
                  isActive={rating === 1}
                  onAnimate={() => rating !== 1 && handleRateClick(1)}
                >
                  {rating === 1 ? (
                    <ThumbUpSelectedIcon width={20} height={20} />
                  ) : (
                    <ThumbUpIcon width={20} height={20} />
                  )}
                </ButtonAnimation>
              </ToolbarButton>

              <ToolbarButton $ratingDown={rating === -1} onClick={() => {}}>
                <ButtonAnimation
                  variant="thumb"
                  color="255, 89, 89"
                  isActive={rating === -1}
                  onAnimate={() => rating !== -1 && handleRateClick(-1)}
                >
                  {rating === -1 ? (
                    <ThumbDownSelectedIcon
                      width={20}
                      height={20}
                      style={{ marginTop: 3 }}
                    />
                  ) : (
                    <ThumbDownIcon
                      width={20}
                      height={20}
                      style={{ marginTop: 3 }}
                    />
                  )}
                </ButtonAnimation>
              </ToolbarButton>
            </ButtonGroup>

            {/* File actions group — settings / folder / download  (mb 20) */}
            <ButtonGroup $mb={20}>
              {!!video?.customPrompt && (
                <ToolbarButton onClick={() => {}}>
                  <ButtonAnimation
                    variant="settings"
                    color="167, 139, 250"
                    onAnimate={handleSettingsClick}
                  >
                    <SettingsIcon
                      width={20}
                      height={20}
                      style={{ marginTop: 2 }}
                    />
                  </ButtonAnimation>
                </ToolbarButton>
              )}

              <ToolbarButton onClick={() => {}}>
                <ButtonAnimation
                  variant="folder"
                  color="251, 191, 36"
                  onAnimate={handleFolderClick}
                >
                  <FolderIcon width={20} height={20} style={{ marginTop: 2 }} />
                </ButtonAnimation>
              </ToolbarButton>

              <ToolbarButton onClick={handleDownload}>
                <LottieIcon
                  animationData={downloadAnimation}
                  trigger="click"
                  size={24}
                  absolute
                />
              </ToolbarButton>

              {video?.isMyModel && (
                <ToolbarButton
                  onClick={() => setShowSetCoverConfirm(true)}
                  aria-label="Set as template cover"
                  title="Set as template cover"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    width={20}
                    height={20}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="m12 3.5 2.59 5.25 5.79.84-4.19 4.08.99 5.77L12 16.77l-5.18 2.72.99-5.77L3.62 9.6l5.79-.84L12 3.5Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </ToolbarButton>
              )}
            </ButtonGroup>

            {/* Thin divider (own margin-bottom 16) */}
            <Divider />

            {/* Delete — standalone destructive action  (mb 32) */}
            <ButtonGroup $mb={32}>
              <ToolbarButton $danger onClick={handleDeleteClick}>
                <LottieIcon
                  animationData={deleteAnimation}
                  trigger="hover"
                  style={{ bottom: 10 }}
                  size={24}
                  absolute
                  strokeColor={"#FF8787"}
                />
              </ToolbarButton>
            </ButtonGroup>
          </Toolbar>

          {/* Primary CTAs — ExplosionButton (same look + behavior as
              the generate buttons elsewhere: brand gradient pill, idle
              glow pulse, scan-line during loading, splash on action). */}
          <ActionRow>
            {video?.output?.type === MEDIA_TYPE.VIDEO && (
              <ExplosionButton
                style={{ display: "block", width: "100%" }}
                disabled={isAnyLoading}
                isLoading={loading}
                onClick={() => {
                  dispatch(setSelectedExploreCollectionFilter("EXTEND_WITH"));
                  dispatch(fetchCollection({ collectionId: video._id }))
                    .unwrap()
                    .then(() => {
                      setShowExtendVideoModal(true);
                    })
                    .catch(() => {
                      setShowExtendVideoModal(true);
                    });
                }}
              >
                {isMobile ? "Extend" : "Extend Video"}
                <GenerateDrop aria-hidden="true" />
                30
              </ExplosionButton>
            )}

            {video?.output?.type === MEDIA_TYPE.IMAGE && (
              <ExplosionButton
                style={{ display: "block", width: "100%" }}
                disabled={isAnyLoading}
                isLoading={loading}
                onClick={() => {
                  dispatch(setSelectedExploreCollectionFilter("EXTEND_WITH"));
                  dispatch(fetchCollection({ collectionId: video._id }))
                    .unwrap()
                    .then(() => {
                      setShowExtendVideoModal(true);
                    })
                    .catch(() => {
                      setShowExtendVideoModal(true);
                    });
                }}
              >
                Animate
                <GenerateDrop aria-hidden="true" />
                30
              </ExplosionButton>
            )}

            <ExplosionButton
              style={{ display: "block", width: "100%" }}
              disabled={isAnyLoading}
              isLoading={regenerateLoading || regenerateLoading2}
              onClick={regenerateVideo}
            >
              {isMobile ? "Regenerate" : "Generate Again"}
              <GenerateDrop aria-hidden="true" />
              {isImageOutput(video.modelType)
                ? Math.ceil(video.price * (1 - REGENERATE_DISCOUNT_PERCENTAGE))
                : Math.ceil(
                    (video.price +
                      (video.nudify ? NUDIFY_PRICE : 0) +
                      VIDEO_RESOLUTION_PRICE[video.output.resolution] +
                      (VIDEO_DURATION_PRICE[video.output.videoDuration] ?? 0) +
                      (VIDEO_FPS_PRICE[video.output.fps] ?? 0)) *
                      (1 - REGENERATE_DISCOUNT_PERCENTAGE),
                  )}
            </ExplosionButton>
          </ActionRow>
        </Footer>
      </ModalContainer>

      <ConfirmationModal
        isOpen={showSetCoverConfirm}
        onClose={() => !selectCoverPending && setShowSetCoverConfirm(false)}
        onConfirm={handleSetCoverConfirm}
        title="Set as template cover"
        message="Do you want to use this image as the cover for your template?"
        confirmText="Set as cover"
        cancelText="Cancel"
        confirmButtonType="primary"
        isLoading={selectCoverPending}
      />
    </Modal>
  );
};

export default CollectionModal;
